import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useFormatTimeDifference } from 'src/utils/formatTimeDifference'
import type { MarbleGame } from 'src/types/domain/Game'
import type { Race } from 'src/types/domain/Race'
import {
  isRaceEndedEvent,
  isRaceSettledEvent,
  isRaceStartedEvent
} from 'src/types/domain/RaceEvent'
import { getSortedRaceResults } from 'src/utils/raceUtils'
import { getRaceRoundId } from 'src/utils/getRaceRoundId'
import FlagCheckeredIcon from 'src/app/assets/icons/ico-flag-checkered.svg'
import LightningIcon from 'src/app/assets/icons/ico-bolt-lightning.svg'
import { getOrdinalSuffix } from 'src/utils/getOrdinalSuffix'
import { useClientSideTranslation } from 'src/services/i18n'
import { getTranslatedMarbleColor } from 'src/services/i18n/utils/gameTranslations'
import { useGetCurrentLanguage, useGetGameConfig } from 'src/store/game'
import { isKnownGameConfig } from 'src/types/domain/GameConfig'
import { MarbleItem } from 'src/components/MarbleItem'
import { MarblePositionLabel } from 'src/components/MarblePositionLabel'
import { isCrashGameType, isCCTVGameType } from 'src/utils/gameTypeUtils'

type Props = Readonly<{
  race: Race
  game: MarbleGame
  // Opens the round's provably-fair modal — the same CrashRoundModal the
  // history pills open. Provided per-ROUND (crash or counting) whenever that
  // round published a commitment or a reveal; when present the compact card
  // becomes tappable.
  onOpenFairness?: () => void
}>

export function PastRace({ race, game, onOpenFairness }: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const formatTimeDifference = useFormatTimeDifference()

  const settledEvent = race.raceEvents.find(isRaceSettledEvent)
  const raceEndTime = settledEvent?.eventTimestamp

  // CCTV-only: pull the location off the settled event's
  // streamMetadata. The engine (irl-vehicle-counter) attaches
  // location + streamKey + detectionType at result time. Absent for
  // non-CCTV games and for any pre-deploy CCTV rounds — gate the
  // location pill render on both isCCTVGameType + presence.
  // Crash is grouped with CCTV for music-suppression only — it is NOT a
  // counting/boundary game, so it must never surface the counting-round
  // location pill even if its clip payload carries streamMetadata.
  const cctvLocation =
    isCCTVGameType(game.track.gameType) &&
    !isCrashGameType(game.track.gameType) &&
    settledEvent?.payload?.streamMetadata?.location
      ? settledEvent.payload.streamMetadata.location
      : null

  // CCTV-only: per-option boundary labels from the race_started
  // payload (e.g. "Under 12" / "13-15" / "Over 16" / "12 or 16").
  // Used to swap the winning marble's colour name for the boundary
  // range so the past-races row reads as the actual outcome
  // ("Under 12") rather than the abstract option name ("Under").
  // Empty for non-CCTV games and pre-deploy CCTV rounds.
  const cctvOptionLabels: Record<string, string> = (() => {
    // Crash (CCTV-grouped for music only) has no boundary option labels.
    if (!isCCTVGameType(game.track.gameType) || isCrashGameType(game.track.gameType)) return {}
    const startedEvent = race.raceEvents.find(isRaceStartedEvent)
    return startedEvent?.payload?.optionLabels ?? {}
  })()

  const marbleResults = getSortedRaceResults(race, game.marbles)

  // Check if this is a crash-style game (multiplier instead of marble results)
  const isCrashGame = isCrashGameType(game.track.gameType)

  // Single-result games use simplified layout (coin_flip, stairpong, fishtank, irl, irl_alt, fast_lane, crash games)
  const isSingleResultGame =
    game.track.gameType === 'coin_flip' ||
    game.track.gameType === 'stairpong' ||
    game.track.gameType === 'fishtank' ||
    game.track.gameType === 'irl' ||
    game.track.gameType === 'irl_alt' ||
    game.track.gameType === 'duck_river' ||
    game.track.gameType === 'snow_run' ||
    game.track.gameType === 'social_betting' ||
    game.track.gameType === 'fast_lane' ||
    isCrashGame

  const isLightning = Boolean(race.isLightningRound && race.lightningBoostType)

  // Resolve which marble(s) were boosted on this round so the footer
  // can show them. The IDs are marble UUIDs (per supabase
  // `lightning_boosted_items`); we look them up against the live
  // `game.marbles` list.
  const boostedMarbles =
    race.lightningBoostedItems && race.lightningBoostedItems.length > 0
      ? race.lightningBoostedItems
          .map((id) => game.marbles.find((m) => m.uuid === id))
          .filter((m): m is NonNullable<typeof m> => Boolean(m))
      : []

  // Boost factor as a `x1.5`-style label (additive percent on the
  // wire → effective multiplier), trimmed of trailing zeros so x1.50
  // reads as x1.5. Same convention as the chip-placement panel's
  // boost pill.
  const boostFactor =
    race.lightningBoostPercent && race.lightningBoostPercent > 0
      ? 1 + race.lightningBoostPercent / 100
      : null
  const boostFactorLabel = boostFactor
    ? boostFactor % 1 === 0
      ? `x${boostFactor.toFixed(0)}`
      : `x${boostFactor.toFixed(2).replace(/\.?0+$/, '')}`
    : null

  // Lightning footer — small sub-row that appears under the main
  // entry for lightning rounds. Reads as `⚡ x1.5 on (marble)` so
  // the player can see WHICH marble was boosted (separate from the
  // winner shown above) AND how strong the boost was, without the
  // information cluttering the main row.
  const lightningFooter =
    isLightning && (boostFactorLabel || boostedMarbles.length > 0) ? (
      <div className="past-race-lightning-footer">
        <LightningIcon className="past-race-lightning-footer__bolt" />
        {boostFactorLabel && (
          <span className="past-race-lightning-footer__factor">{boostFactorLabel}</span>
        )}
        {boostedMarbles.length > 0 && (
          <>
            <span className="past-race-lightning-footer__on">{t('on')}</span>
            <span className="past-race-lightning-footer__marbles">
              {boostedMarbles.map((marble) => (
                <MarbleItem
                  className={`marble-${marble.color} past-race-lightning-footer__marble`}
                  key={marble.uuid}
                  marble={marble}
                  tooltip={getTranslatedMarbleColor(marble.color, t)}
                />
              ))}
            </span>
          </>
        )}
      </div>
    ) : null

  // CCTV-only: small pin showing the stream location used at the
  // round's result time. Same compact footer pattern as lightning;
  // renders below the main row on settled rounds. Absent for
  // non-CCTV games and for pre-deploy rounds where the engine
  // hadn't started attaching streamMetadata yet.
  const cctvLocationFooter = cctvLocation ? (
    <div className="past-race-cctv-footer">
      <span className="past-race-cctv-footer__location">{cctvLocation}</span>
    </div>
  ) : null

  // Configured side-bet markets (Even/Odd, Exact-Boundary). When a
  // round emits `marketResults`, surface each market's winner as a
  // small chip in a compact footer row — same visual rhythm as the
  // location pill so the past-races list keeps a consistent footer
  // chassis. Markets with no winner this round (Exact-Boundary on a
  // non-jackpot round) are skipped via the marketResults map check.
  const gameConfig = useGetGameConfig()
  const sideBetOutcomes = (() => {
    const marketResults = settledEvent?.payload.marketResults
    if (!marketResults)
      return [] as Array<{
        marketId: string
        marketLabel: string
        marble: NonNullable<ReturnType<typeof game.marbles.find>>
        label: string
      }>
    if (!isKnownGameConfig(gameConfig) || !gameConfig.markets) return []

    const sideBetMarkets = gameConfig.markets.filter((m) => m.type === 'side_bet')
    const out: Array<{
      marketId: string
      marketLabel: string
      marble: NonNullable<ReturnType<typeof game.marbles.find>>
      label: string
    }> = []
    for (const market of sideBetMarkets) {
      const winner = marketResults[market.id]?.find((r) => r.position === 1)
      if (!winner) continue
      const marble = game.marbles.find((m) => m.uuid === winner.marbleId)
      if (!marble) continue
      // Prefer the per-round CCTV label (e.g. "Exact 12") over the
      // marble's static color when present; falls back to the marble's
      // translated colour name for Even/Odd which doesn't carry a
      // per-round label.
      const label = cctvOptionLabels[marble.uuid] ?? getTranslatedMarbleColor(marble.color, t)
      out.push({
        marketId: market.id,
        marketLabel: market.label,
        marble,
        label
      })
    }
    return out
  })()
  const sideBetFooter =
    sideBetOutcomes.length > 0 ? (
      <div className="past-race-sidebet-footer">
        {sideBetOutcomes.map(({ marketId, marketLabel, marble, label }) => (
          <div className="past-race-sidebet-footer__entry" key={marketId} title={marketLabel}>
            <MarbleItem
              className={`marble-${marble.color} past-race-sidebet-footer__marble`}
              marble={marble}
              tooltip={getTranslatedMarbleColor(marble.color, t)}
            />
            <span className="past-race-sidebet-footer__label">{label}</span>
          </div>
        ))}
      </div>
    ) : null

  // A round with a provable-fairness story is tappable — the whole row opens the
  // round's fairness modal. Shared by BOTH layouts (the compact single-result row
  // AND the podium marble layout below): the podium games (Plinko / Snake /
  // Rolling Dunes) render the multi-place layout, so without this here their rows
  // were not tappable and the modal could not be opened for them.
  const fairnessProps = onOpenFairness
    ? {
        role: 'button' as const,
        tabIndex: 0,
        'aria-label': `${t('Round details')}: ${getRaceRoundId(race.round, game.track.shortName)}`,
        onClick: onOpenFairness,
        onKeyDown: (e: ReactKeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onOpenFairness()
          }
        }
      }
    : {}

  // Simplified layout for single-result games - main row with
  // result/round-id/time, plus an optional lightning footer below.
  if (isSingleResultGame) {
    const winningMarble = marbleResults?.[0]?.marble
    const resultClass = winningMarble?.color ? `result-${winningMarble.color}` : ''

    // For crash games, get the crash multiplier from the race_ended event
    const raceEndedEvent = race.raceEvents.find((e) => e.eventType === 'race_ended')
    const crashMultiplier =
      isCrashGame && raceEndedEvent && isRaceEndedEvent(raceEndedEvent)
        ? raceEndedEvent.payload?.crash_multiplier
        : undefined

    // Determine crash result class based on multiplier tier
    const getCrashResultClass = () => {
      if (!isCrashGame) return resultClass
      if (!crashMultiplier) return 'result-crash-low'
      if (crashMultiplier >= 10) return 'result-crash-huge'
      if (crashMultiplier >= 5) return 'result-crash-high'
      if (crashMultiplier >= 2) return 'result-crash-good'
      if (crashMultiplier >= 1.5) return 'result-crash-mid'
      return 'result-crash-low'
    }

    // Compact card layout — collapses the four stacked rows
    // (main / lightning / cctv / sidebet) of the original layout into
    // a single 3-section card. ~58px tall vs ~120px so 8-10 rounds fit
    // per phone screen, and the higher density also reads as cleaner
    // and more Evolution-like on desktop.
    const lightningTierClass =
      isLightning && race.lightningBoostType
        ? `past-race-compact--lightning-${race.lightningBoostType}`
        : ''

    // Compact relative-time formatter (`35s`, `2m`, `1h`, `3d`) — the
    // i18n'd `formatTimeDifference` produces "8 min 35 sec ago" which
    // is too verbose for the dense footer. Universal abbreviations
    // match the Evolution-style aesthetic and don't need translating.
    const compactTime = (timestamp: string): string => {
      const seconds = Math.max(0, Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000))
      if (seconds < 60) return `${seconds}s`
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
      if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
      return `${Math.floor(seconds / 86400)}d`
    }

    // Compact card is tappable when a fairness opener is present (fairnessProps
    // is defined once above and shared with the podium layout).
    return (
      <div
        className={`past-race-compact ${getCrashResultClass()} ${
          isCrashGame ? 'past-race-compact--crash' : ''
        } ${
          isLightning ? `past-race-compact--lightning ${lightningTierClass}` : ''
        } ${onOpenFairness ? 'past-race-compact--tappable' : ''}`}
        key={race.uuid}
        {...fairnessProps}
      >
        <div className="past-race-compact__primary">
          {isCrashGame ? (
            <span className="past-race-compact__crash">
              {crashMultiplier ? `${crashMultiplier.toFixed(2)}x` : t('Crashed')}
            </span>
          ) : (
            <>
              {winningMarble && (
                <MarbleItem
                  className={`marble-${winningMarble.color} past-race-compact__marble`}
                  marble={winningMarble}
                  tooltip={getTranslatedMarbleColor(winningMarble.color, t)}
                />
              )}
              <span className="past-race-compact__outcome">
                {winningMarble
                  ? (cctvOptionLabels[winningMarble.uuid] ??
                    getTranslatedMarbleColor(winningMarble.color, t))
                  : ''}
              </span>
            </>
          )}
        </div>
        {sideBetOutcomes.length > 0 && (
          <div className="past-race-compact__secondary">
            {sideBetOutcomes.map(({ marketId, marble, label }, idx) => (
              <span className="past-race-compact__sidebet" key={marketId}>
                {idx > 0 && (
                  <span className="past-race-compact__sep" aria-hidden>
                    ·
                  </span>
                )}
                <MarbleItem
                  className={`marble-${marble.color} past-race-compact__sidebet-marble`}
                  marble={marble}
                  tooltip={getTranslatedMarbleColor(marble.color, t)}
                />
                {label}
              </span>
            ))}
          </div>
        )}
{(boostFactorLabel || boostedMarbles.length > 0) && (
          // Its own line. It cannot share the meta row: that row already
          // carries a location on the CCTV games ("Pine Trail #SRU15692"), and
          // the badge ate exactly the space the location needed. Indented to
          // the same column as the meta line below and the outcome text above,
          // so the card keeps one clean left edge — and with no rule across
          // the card, which read as splitting it in two.
          <div className="past-race-compact__boost-row">
            <span className="past-race-compact__boost">
              <LightningIcon className="past-race-compact__boost-icon" />
              {boostFactorLabel}
              {boostedMarbles.map((marble) => (
                <MarbleItem
                  className={`marble-${marble.color} past-race-compact__boost-marble`}
                  key={marble.uuid}
                  marble={marble}
                  tooltip={getTranslatedMarbleColor(marble.color, t)}
                />
              ))}
            </span>
          </div>
        )}
                <div className="past-race-compact__footer">
          <span className="past-race-compact__footer-meta">
            {cctvLocation && <span className="past-race-compact__location">{cctvLocation}</span>}
            <span className="past-race-compact__round-id">
              {getRaceRoundId(race.round, game.track.shortName)}
            </span>
          </span>
          {raceEndTime && (
            <span className="past-race-compact__time">{compactTime(raceEndTime)}</span>
          )}
        </div>
      </div>
    )
  }

  // Default layout for other games (marbles racing). Tappable to open the
  // fairness modal when the round has a reveal — same affordance as the compact
  // layout, wired here so the podium games (Plinko / Snake / Rolling Dunes) can
  // open it too.
  return (
    <div
      className={`entry ${isLightning ? 'entry--lightning' : ''} ${isLightning && race.lightningBoostType ? `entry--lightning-${race.lightningBoostType}` : ''} ${onOpenFairness ? 'entry--tappable' : ''}`}
      key={race.uuid}
      {...fairnessProps}
    >
      <div className="top-bar">
        <div className="race-id">
          <FlagCheckeredIcon />
          {getRaceRoundId(race.round, game.track.shortName)}
        </div>
        <div className="time">{raceEndTime ? formatTimeDifference(raceEndTime) : ''}</div>
      </div>
      <div className="bottom-bar bottom-bar--past-races">
        <span className="label">{t('Results')}</span>
        <div className="choose-marbles-order show-single-position-compact">
          {marbleResults?.map(({ marble, position }) => (
            <div className="entry-order entry-order-marble-spin" key={position}>
              <div className="entry-marble">
                <MarbleItem
                  className={`marble-${marble.color}`}
                  key={marble.uuid}
                  marble={marble}
                  tooltip={getTranslatedMarbleColor(marble.color, t)}
                />
              </div>
              <div className="entry-selector">
                <div className={`select ${getOrdinalSuffix(position)} selected`}>
                  <MarblePositionLabel position={position} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {lightningFooter}
      {cctvLocationFooter}
      {sideBetFooter}
    </div>
  )
}
